/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import { uiActions, uiTypes, uiSelectors } from '@opengeoweb/store';
import { ParameterInfoButton } from './ParameterInfoButton';
import { timeSeriesActions, timeSeriesSelectors } from '../../../store';
import type { TimeSeriesRootState } from '../../../store/types';
import type { Parameter } from '../../TimeSeries/types';

interface ParameterInfoButtonProps {
  parameter: Parameter;
  panelId: string;
}

export const ParameterInfoButtonConnect: React.FC<ParameterInfoButtonProps> = ({
  parameter,
  panelId,
}: ParameterInfoButtonProps) => {
  const dispatch = useDispatch();

  const currentSourcePanelId = useSelector((store: CoreAppStore) =>
    uiSelectors.getSourcePanelId(store, uiTypes.DialogTypes.TimeSeriesInfo),
  );
  const isOpenInStore = useSelector((store: CoreAppStore) =>
    uiSelectors.getisDialogOpen(store, uiTypes.DialogTypes.TimeSeriesInfo),
  );

  const currentParameterDisplayed = useSelector((store: TimeSeriesRootState) =>
    timeSeriesSelectors.getParameterInfoParameter(store, panelId),
  );

  const isActive = parameter.id
    ? parameter.id === currentParameterDisplayed?.id
    : parameter.propertyName === currentParameterDisplayed?.propertyName &&
      parameter.serviceId === currentParameterDisplayed?.serviceId &&
      parameter.collectionId === currentParameterDisplayed?.collectionId;

  const toggleInfoDialog = React.useCallback((): void => {
    dispatch(
      uiActions.setToggleOpenDialog({
        type: uiTypes.DialogTypes.TimeSeriesInfo,
        setOpen: currentSourcePanelId !== panelId ? true : !isOpenInStore,
        sourcePanelId: panelId,
      }),
    );
  }, [dispatch, isOpenInStore, currentSourcePanelId, panelId]);

  const setCurrentParameterInfoDisplayed = React.useCallback((): void => {
    dispatch(
      timeSeriesActions.setCurrentParameterInfoDisplayed({
        panelId,
        parameter: parameter.id || parameter,
      }),
    );

    dispatch(
      uiActions.orderDialog({
        type: uiTypes.DialogTypes.TimeSeriesInfo,
      }),
    );
  }, [dispatch, panelId, parameter]);

  const isOpenForPanel = isOpenInStore && currentSourcePanelId === panelId;

  const onClick = useCallback((): void => {
    if (isActive) {
      toggleInfoDialog();
    }
    if (!isActive && isOpenForPanel) {
      setCurrentParameterInfoDisplayed();
    }
    if (!isActive && !isOpenForPanel) {
      setCurrentParameterInfoDisplayed();
      toggleInfoDialog();
    }
  }, [
    isActive,
    toggleInfoDialog,
    isOpenForPanel,
    setCurrentParameterInfoDisplayed,
  ]);

  return (
    <ParameterInfoButton
      isActive={isActive}
      isOpenInStore={isOpenForPanel}
      onClick={onClick}
    />
  );
};
